/**
 * B2C Consumer Authentication Service
 *
 * This service handles authentication for consumer/borrower users via
 * Stytch Consumer API (not B2B). All endpoints use the /api/v1/b2c/auth/*
 * prefix.
 *
 * Key differences from B2B (stytchAuthService.js):
 * - Uses Stytch Consumer project (separate from B2B)
 * - No organization membership required
 * - Tenant context passed via signed state in redirect URL
 */
import axios from 'axios';
import { api } from 'constants/constant';

// Axios client for B2C (Consumer) authentication endpoints
const b2cAuthApi = axios.create({
  baseURL: api, // e.g. http://localhost:8001/api/v1
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

// Attach tenant headers for B2C requests (similar to B2B)
b2cAuthApi.interceptors.request.use(
  (config) => {
    if (typeof window !== 'undefined') {
      // Always include the full hostname for tenant resolution
      config.headers['X-Tenant-Host'] = window.location.hostname;

      // Include tenant slugs from URL params or session storage
      const searchParams = new URLSearchParams(window.location.search || '');
      const slug = searchParams.get('slug');
      const branch = searchParams.get('branch') || searchParams.get('branch_slug');

      if (slug) {
        config.headers['X-Tenant-Slug'] = slug.toLowerCase();
      }
      if (branch) {
        config.headers['X-Branch-Slug'] = branch.toLowerCase();
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

/**
 * Extract tenant context from URL or session storage
 * Used for B2C flows where tenant context comes from invite links
 */
export const getTenantContext = () => {
  if (typeof window === 'undefined') {
    return { organizationId: null, branchId: null, loanOfficerId: null };
  }

  const searchParams = new URLSearchParams(window.location.search || '');

  // Try URL params first
  let organizationId = searchParams.get('org_id') || searchParams.get('organization_id');
  let branchId = searchParams.get('branch_id');
  let loanOfficerId = searchParams.get('lo_id') || searchParams.get('loan_officer_id');
  let slug = searchParams.get('slug');
  let branchSlug = searchParams.get('branch') || searchParams.get('branch_slug');

  // Fallback to session storage
  if (typeof sessionStorage !== 'undefined') {
    if (!organizationId) {
      organizationId = sessionStorage.getItem('b2c_organization_id');
    }
    if (!branchId) {
      branchId = sessionStorage.getItem('b2c_branch_id');
    }
    if (!loanOfficerId) {
      loanOfficerId = sessionStorage.getItem('b2c_loan_officer_id');
    }
    if (!slug) {
      slug = sessionStorage.getItem('mira_client_slug');
    }
    if (!branchSlug) {
      branchSlug = sessionStorage.getItem('mira_branch_slug');
    }
  }

  return {
    organizationId,
    branchId,
    loanOfficerId,
    slug,
    branchSlug,
  };
};

/**
 * Store tenant context in session storage for persistence across navigation
 */
export const storeTenantContext = (context) => {
  if (typeof sessionStorage === 'undefined') return;

  if (context.organizationId) {
    sessionStorage.setItem('b2c_organization_id', context.organizationId);
  }
  if (context.branchId) {
    sessionStorage.setItem('b2c_branch_id', context.branchId);
  }
  if (context.loanOfficerId) {
    sessionStorage.setItem('b2c_loan_officer_id', context.loanOfficerId);
  }
};

/**
 * Send magic link to consumer email
 *
 * Unlike B2B, this doesn't require an organization_id from Stytch.
 * The tenant context (org, branch, LO) is encoded in a signed state
 * parameter by the backend.
 *
 * @param {string} email - Consumer email address
 * @param {Object} metadata - Optional tenant context
 * @param {string} metadata.organizationId - Database organization UUID
 * @param {string} metadata.branchId - Branch UUID
 * @param {string} metadata.loanOfficerId - Loan officer user ID
 * @returns {Promise} Axios response
 */
export const sendConsumerMagicLink = (email, metadata = {}) => {
  // Get tenant context from params or provided metadata
  const tenantContext = getTenantContext();
  const organizationId = metadata.organizationId || tenantContext.organizationId;
  const branchId = metadata.branchId || tenantContext.branchId;
  const loanOfficerId = metadata.loanOfficerId || tenantContext.loanOfficerId;
  const organizationSlug = metadata.organizationSlug || tenantContext.slug;
  const branchSlug = metadata.branchSlug || tenantContext.branchSlug;

  return b2cAuthApi.post('/b2c/auth/magic-link/send', {
    email,
    organization_id: organizationId,
    branch_id: branchId,
    loan_officer_id: loanOfficerId,
    organization_slug: organizationSlug,
    branch_slug: branchSlug,
  });
};

/**
 * Authenticate consumer magic link token
 *
 * This is typically handled by the backend callback endpoint,
 * but can be called directly for custom flows.
 *
 * @param {string} token - Stytch magic link token
 * @param {Object} statePayload - Optional state from signed state parameter
 * @returns {Promise} Axios response
 */
export const authenticateConsumerToken = (token, statePayload = {}) => {
  return b2cAuthApi.post('/b2c/auth/magic-link/authenticate', {
    token,
    ...statePayload,
  });
};

/**
 * Get current consumer user
 *
 * Fetches the authenticated consumer's profile from the session.
 * Requires valid session cookie.
 *
 * @returns {Promise} Axios response with user data
 */
export const fetchConsumerUser = () => {
  return b2cAuthApi.get('/b2c/auth/me');
};

// Alias for compatibility with existing code
export const fetchCurrentConsumer = fetchConsumerUser;

/**
 * Fetch tenant context from the public B2C apply endpoint
 *
 * Useful when the callback URL only includes slug/branch and
 * we need organization_id/branch_id for session exchange.
 */
export const fetchApplyContext = () => {
  return b2cAuthApi.get('/b2c/apply');
};

/**
 * Revoke consumer session (logout)
 *
 * Invalidates the current session and clears cookies.
 *
 * @returns {Promise} Axios response
 */
export const revokeConsumerSession = () => {
  return b2cAuthApi.post('/b2c/auth/session/revoke', {});
};

/**
 * Complete consumer onboarding
 *
 * Updates the consumer's profile with required information
 * after initial authentication, including loan purpose and employment type.
 *
 * @param {Object} payload - Onboarding data
 * @param {string} payload.user_id - Consumer user ID (UUID)
 * @param {string} payload.first_name - Consumer's first name (optional)
 * @param {string} payload.last_name - Consumer's last name (optional)
 * @param {string} payload.phone_number - Consumer's phone (optional)
 * @param {string} payload.loan_purpose - Loan purpose: 'purchase' or 'refinance'
 * @param {string} payload.employment_type - Employment type: 'employed_ft', 'employed_pt', 'self_employed', 'retired', 'seasonal'
 * @returns {Promise} Axios response
 */
export const completeConsumerOnboarding = (payload) => {
  return b2cAuthApi.post('/b2c/auth/complete-onboarding', payload);
};

export const completeCoBorrowerOnboarding = (payload) => {
  return b2cAuthApi.post('/b2c/loan/co_borrower/complete-onboarding', payload);
};

/**
 * Exchange Stytch token for session JWT (B2C flow)
 *
 * This is the session exchange endpoint with B2C-specific parameters.
 *
 * @param {Object} payload - Exchange payload
 * @param {string} payload.token - Stytch token
 * @param {string} payload.stytch_token_type - Token type (magic_links, sessions)
 * @param {string} payload.organization_id - Database org UUID
 * @param {string} payload.branch_id - Branch UUID
 * @param {string} payload.loan_officer_id - Loan officer ID
 * @returns {Promise} Axios response with session data
 */
export const exchangeB2CSession = (payload) => {
  return b2cAuthApi.post('/auth/session/exchange', {
    ...payload,
    flow: 'b2c',
  });
};

/**
 * Validate tenant (org/branch) slugs before loading the login page.
 *
 * Calls the backend to verify that the organization slug (from the hostname)
 * exists in the database. The backend checks `stytch_organization_slug` and
 * handles region suffixes like `-ca` automatically.
 *
 * @param {string} orgSlug - Organization slug extracted from hostname
 * @param {string|null} branchSlug - Branch slug extracted from hostname (optional)
 * @returns {Promise} Axios response with { valid, organization_name, organization_id, branch_name, branch_id }
 */
export const validateTenant = (orgSlug, branchSlug = null) => {
  const params = { org_slug: orgSlug };
  if (branchSlug) {
    params.branch_slug = branchSlug;
  }
  return b2cAuthApi.get('/b2c/auth/validate-tenant', { params });
};

export default {
  sendConsumerMagicLink,
  authenticateConsumerToken,
  fetchConsumerUser,
  fetchCurrentConsumer, // Alias for compatibility
  revokeConsumerSession,
  completeConsumerOnboarding,
  exchangeB2CSession,
  fetchApplyContext,
  getTenantContext,
  storeTenantContext,
  validateTenant,
};
